<template>
  <v-dialog v-model="dialogState" max-width="500px">
    <v-card>
      <template #title>
        <span class="text-primary">{{ t("components.refundDialog.title") }}</span>
      </template>
      <v-card-text class="pb-0">
        <p v-if="props.transaction!.status !== 'partially-refunded'" class="mb-4">
          {{
            t("components.refundDialog.fullAmount", {
              amount: displayAmount(props.transaction!.amount!),
            })
          }}
        </p>
        <p v-else class="mb-4">
          {{
            t("components.refundDialog.partialAmount", {
              refunded: displayAmount(leftToRefundAmount),
              remaining: displayAmount(maxRefundAmount),
            })
          }}
        </p>
        <p class="mb-6">
          {{ t("components.refundDialog.pspReference") }}:
          <kbd>{{ props.transaction!.pspReference }}</kbd
          ><br />
          {{ t("components.refundDialog.transactionDate") }}: {{ props.transaction!.createdDate
          }}<br />
          {{ t("components.refundDialog.fullAmountLabel") }}:
          {{ displayAmount(props.transaction!.amount!) }}
        </p>
        <v-text-field
          v-model.number="amountToRefund"
          type="number"
          :min="0"
          :max="Math.ceil(maxRefund)"
          :prefix="getCurrencySymbol('en-UK', props.transaction?.amount?.currency ?? 'GBP')"
          :label="t('components.refundDialog.amountToRefund')"
          :error-messages="
            (v$.amountToRefund.$dirty &&
              v$.amountToRefund.$invalid &&
              v$.amountToRefund.$silentErrors[0].$message.toString()) ||
            ''
          "
        />
      </v-card-text>
      <template #actions>
        <div class="d-flex flex-column w-100 px-4 pb-3">
          <v-text-field
            v-model="emailToSendReceipt"
            class="w-100"
            :label="t('components.refundDialog.emailReceipt')"
            type="email"
            autocapitalize="none"
            autocomplete="email"
            clearable
            :error-messages="
              (v$.emailToSendReceipt.$dirty &&
                v$.emailToSendReceipt.$invalid &&
                v$.emailToSendReceipt.$silentErrors[0].$message.toString()) ||
              ''
            "
          />
          <div class="d-flex w-100 mt-4">
            <v-btn-nd class="" @click="dialogState = !dialogState">
              {{ t("form.cancel") }}
            </v-btn-nd>
            <v-spacer />
            <v-btn
              class="bg-primary on-primary"
              :disabled="v$.emailToSendReceipt.$invalid || v$.amountToRefund.$invalid"
              :loading="transactionsStore.refundLoading"
              @click="refund()"
            >
              {{ t("form.submit") }}
            </v-btn>
          </div>
        </div>
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { helpers, email as emailValidator } from "@vuelidate/validators";
import BigNumber from "bignumber.js";
import { ref, computed, watch, reactive, onUpdated } from "vue";
import { useI18n } from "vue-i18n";
import { Amount } from "@/api/merchant-service/common-types";
import { TransactionV1 } from "@/api/merchant-service/transaction";
import { useApplicationStore } from "@/store/application";
import { useTransactionsStore } from "@/store/transactions";
import { displayAmount } from "@/utils/format";

const { t } = useI18n();

const dialogState = defineModel<boolean>({ required: true });
const props = defineProps<{ transaction?: TransactionV1 }>();

const transactionsStore = useTransactionsStore();
const applicationStore = useApplicationStore();

const maxRefund = computed(() => {
  const sumRefunds =
    props.transaction?.refunds?.reduce((acc, refund) => acc + refund.amount.value, 0) ?? 0;
  const amountLeft = new BigNumber(props.transaction?.amount?.value ?? 0)
    .minus(sumRefunds)
    .toNumber();
  return new BigNumber(amountLeft).dividedBy(100).toNumber();
});
const maxRefundAmount = computed<Amount>(
  () =>
    ({
      value: maxRefund.value * 100,
      currency: props.transaction?.amount?.currency,
    }) as Amount,
);
const leftToRefundAmount = computed<Amount>(
  () =>
    ({
      value: props.transaction!.amount!.value - maxRefund.value * 100,
      currency: props.transaction?.amount?.currency,
    }) as Amount,
);
const amountToRefund = ref(0);
onUpdated(() => (amountToRefund.value = maxRefund.value));
watch(amountToRefund, (value) => {
  if (value > maxRefund.value) amountToRefund.value = maxRefund.value;
});

const emailToSendReceipt = ref(undefined);
const v$ = useVuelidate(
  {
    emailToSendReceipt: {
      $autoDirty: true,
      email: helpers.withMessage(t("form.emailInvalid"), emailValidator),
    },
    amountToRefund: {
      $autoDirty: true,
      required: helpers.withMessage(
        t("form.required"),
        helpers.withMessage(
          t("components.refundDialog.amountGreaterThanZero"),
          (value: number) => value > 0,
        ),
      ),
      maxValue: helpers.withMessage(
        t("components.refundDialog.amountLessThanMax"),
        (value: number) => value <= maxRefund.value,
      ),
    },
  },
  reactive({
    emailToSendReceipt,
    amountToRefund,
  }),
);

const refund = async () => {
  if (!(await v$.value.$validate())) {
    return;
  }

  if (!props.transaction || !props.transaction.amount) return;
  const amount: Amount = {
    value: new BigNumber(amountToRefund.value).multipliedBy(100).toNumber(),
    currency: props.transaction.amount.currency,
  };

  try {
    await transactionsStore.refundTransaction(
      props.transaction.pspReference,
      amount,
      emailToSendReceipt.value,
    );
    const message = transactionsStore.refundError
      ? t("components.refundDialog.error", { error: transactionsStore.refundError })
      : t("components.refundDialog.success");
    if (transactionsStore.refundReceiptError) {
      console.error(transactionsStore.refundReceiptError);
    }
    applicationStore.notifyUser({
      message,
      type: transactionsStore.refundError ? "error" : "success",
    });
    emailToSendReceipt.value = undefined;
    dialogState.value = false;
  } catch {
    applicationStore.notifyUser({
      message: t("components.refundDialog.genericError"),
      type: "error",
    });
  }
};

const getCurrencySymbol = (locale: string, currency: string) =>
  (0)
    .toLocaleString(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, "")
    .trim();
</script>
